import { autoinject, transient } from 'aurelia-dependency-injection';
import { DepartmentModel, PersistDepartmentRequest } from "../../../../../models/manage/departments.models";
import { ManageDepartmentsClientService } from '../../../../../services/manage/manage-departments.service';
import { UnitOfWorkObserver } from '../../../../../utilities/unit-of-work-observer';
import { NotificationType, NotificationClientService } from '../../../../../services/basics/notification-client.service';
import { I18N } from 'aurelia-i18n';
import { BindingEngine, computedFrom } from 'aurelia-binding';
import { Guid } from '../../../../../utilities/guid';
import { Reflection } from '../../../../../utilities/reflection';


@autoinject
@transient()
export class ManageDepartment {

    private _departmentInEditUow: UnitOfWorkObserver<DepartmentModel>;
    private _cancelOperation: () => void;
    private _saveOperation: () => void;

    public title: string = "";
    public department: DepartmentModel;
    public departmentId: number;
    public branchId: number;

    constructor(private manageDepartmentsClientService: ManageDepartmentsClientService,
        private notificationService: NotificationClientService,
        private i18N: I18N,
        private bindingEngine: BindingEngine) { }


    areThereUnsavedChanges() {
        const changes = this._departmentInEditUow.getChanges();
        if (!this._departmentInEditUow.hasChanges() || (this._departmentInEditUow.hasChanges && Object.keys(changes).length == 1 && changes['identifier'])) {
            return false;
        }
        else {
            return true;
        }
    }

    save() {
        return new Promise<void>((resolve, reject) => {
            if (!this.areThereUnsavedChanges()) {
                this.notificationService.show(this.i18N.tr('no-changes-performed-text'), this.i18N.tr('there-were-no-changes-to-save-text'), NotificationType.Info);
                resolve();
                return;
            }

            if (!this.department.name) {
                this._departmentInEditUow.addChange(x => x.name, this.i18N.tr('default-department-text'), 0);
            }

            if (this.department.departmentId == 0) {
                this._departmentInEditUow.addChange(x => x.isActive, true, false);
                if (this.branchId) this._departmentInEditUow.addChange(x => x.branchId, this.branchId, 0);
            }

            const request: PersistDepartmentRequest = {
                changes: this._departmentInEditUow.getChanges(),
                id: this.department.departmentId
            }

            this.manageDepartmentsClientService.saveDepartment(request)
                .then(() => {
                    this._saveOperation();
                    this.notificationService.show(this.i18N.tr('successfully-saved-text'), this.i18N.tr('department-has-been-successfully-saved-text'), NotificationType.Success);
                    resolve();
                })
                .catch(() => {
                    this.notificationService.show(this.i18N.tr('error-text') + "!", this.i18N.tr('department-cannot-be-saved-text'), NotificationType.Error);
                    reject();
                });
        });
    }

    cancel() {
        this._cancelOperation();
    }

    getEmptyDepartment(): DepartmentModel {
        return {
            departmentId: 0,
            managerId: null,
            name: "",
            isActive: true,
            isSelected: false,
            isDefault: false,
            sequenceNumber: 0,
            guid: Guid.generateGuid(),
            permissions: [],
            roles: [],
            branchId: 0,
            parentDepartmentId: null,
            scope: "",
            identifier: Guid.generateGuid()
        }
    }

    branchIdChanged() {
        if (this.department && !this.department.branchId && this.branchId) {
            this._departmentInEditUow.addChange(x => x.branchId, this.branchId, 0);
        }
    }

    activate(model: { branchId: number, departmentId: number, cancelOperation: () => void, saveOperation: () => void, title: string }) {
        this.branchId = model.branchId;
        this.departmentId = model.departmentId;
        this._cancelOperation = model.cancelOperation;
        this._saveOperation = model.saveOperation;
        this.title = model.title;
        this.departmentIdChanged();
    }

    departmentIdChanged() {
        if (this.departmentId <= 0) {
            this.department = this.getEmptyDepartment();

            if (this._departmentInEditUow) {
                this._departmentInEditUow.dispose();
            }

            this._departmentInEditUow = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.department);
        }
        else if (this.departmentId) {
            this.manageDepartmentsClientService.getDepartment({ departmentId: this.departmentId })
                .then(x => {
                    this.department = x.department;

                    if (this._departmentInEditUow) {
                        this._departmentInEditUow.dispose();
                    }

                    this._departmentInEditUow = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.department);
                })
        }
    }
    
    @computedFrom(Reflection.getPropertyPath<ManageDepartment>(z => z.branchId),
        Reflection.getPropertyPath<ManageDepartment>(z => z.department.departmentId))
    get departmentParameters(): string {
        if (this.department) {
            return this.branchId + "," + this.department.departmentId;
        }
    }
}
